/**
 * When an app's missing closure webhook is a PROBLEM, and what to say after a
 * save that moved queued closures (RidgeWill/auth#236).
 *
 * Extracted rather than left inline in the two components that ask, because the
 * question is asked in two places — a badge in the applications list and a
 * warning inside the detail modal — and they must agree. They did not have to
 * agree while the rule was "the field is empty"; they do now that it is a
 * conjunction, and a rule duplicated as a conjunction is a rule that drifts.
 *
 * ── WHY A MISSING WEBHOOK IS NOT AUTOMATICALLY A GAP ─────────────────
 *
 * Registering an application says which roles may reach it. That is not the same
 * as saying it stores anything about them, and the two only look identical
 * because they coincide for product apps. They part company for our own portals:
 * this admin portal and the account portal are reachable by every role and hold
 * nothing — they read and write auth's database through auth's API. There is no
 * second copy of a user to erase and nothing to POST a closure to.
 *
 * Before `holdsUserData` existed, both carried a permanent "no closure hook"
 * badge that no admin could ever act on. A warning that is always on is a
 * warning nobody reads, including on the rows where it means somebody's personal
 * data is being kept after they asked to be forgotten.
 */
export interface ClosureConfigView {
  active: boolean;
  /**
   * OPTIONAL on purpose, even though the server always sends it.
   *
   * This portal and auth deploy separately, so there is a window in which this
   * build is talking to an auth that predates the field and every app arrives
   * with it `undefined`. Treating that as "holds nothing" would silently switch
   * the warning off across the whole list — the failure would look exactly like
   * everything being correctly configured, which is the one appearance this
   * badge exists to prevent. Absent therefore means TRUE, matching the server's
   * own default: warn, and let an admin untick it.
   */
  holdsUserData?: boolean;
  /** Empty string and null are the same thing here: nowhere to deliver. */
  eventWebhookUrl: string | null;
}

/**
 * All three have to be true.
 *
 * `active` — a retired app carries a louder badge already, and two warnings on
 * one row reads as noise. Retiring does not delete what the app holds, so this
 * is a presentation choice, not a claim that the data is gone.
 *
 * `holdsUserData` — see above. No data, nothing to erase, no gap. Compared
 * against `false` rather than truthily, so an older auth that does not send the
 * field warns rather than falling silent.
 *
 * no URL — the actual gap.
 */
export function needsClosureHook(app: ClosureConfigView): boolean {
  return app.active && app.holdsUserData !== false && !(app.eventWebhookUrl ?? '').trim();
}

/**
 * What to tell the admin after a save, given what the server reported moving.
 *
 * The counts are the part they cannot see from the form, so they are the part
 * worth saying. **Silent when both are zero** — "Saved. 0 closures released"
 * reads as though something was attempted and failed.
 *
 * Both non-zero cannot happen: releasing needs a URL to have been set, and
 * discarding needs custody to have been turned off, and the server only does the
 * second when `holdsUserData === false`. Released is reported first anyway, so a
 * future change that makes both possible degrades to under-reporting rather than
 * to a wrong number.
 */
export function saveOutcome(moved: { requeuedNotices: number; discardedNotices: number }): string {
  const closures = (n: number) => `${n} queued closure${n === 1 ? '' : 's'}`;
  if (moved.requeuedNotices > 0) {
    const it = moved.requeuedNotices === 1 ? 'it' : 'them';
    return `Saved. ${closures(moved.requeuedNotices)} released. The next sweep delivers ${it}.`;
  }
  if (moved.discardedNotices > 0) {
    return `Saved. ${closures(moved.discardedNotices)} discarded.`;
  }
  return 'Saved.';
}
